import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Body1,
  Caption1,
  Title3,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { useResponsiveStyles } from '../hooks/useResponsiveStyles';
import { SPACE, SPACE_X } from '../styles/scale';

/**
 * The app's one page-title pattern — and the app's only `<h1>` (#202).
 *
 * ── The defect this fixes ──
 * Before this component the whole of `apps/web/src` contained exactly ONE
 * heading element: `ErrorBoundary`'s `<h2>`, with no `<h1>` anywhere above it.
 * All six page titles — Dashboard, Admin, the reviewer queue, the PR-creation
 * failure screen and both `RequestForm` states — were a bare `<Title2>`, which
 * renders a `<span>`. Visually a title; to a screen reader, ordinary text. The
 * app therefore had no document outline at all, and no landmark for the "jump
 * to heading" navigation a screen-reader user relies on to skip the Teams
 * chrome. `Title2` also broke the type ramp: 28/36 is reserved for a stat-tile
 * number, so every page shouted one step louder than the scale intends.
 *
 * A page renders exactly ONE of these, and nothing else in the app renders an
 * `<h1>` — the heading level below it is `Subtitle1 as="h2"` for a section.
 *
 * ── Anatomy (three rows; only `title` is required) ──
 *   1. [back link] `Title3 as="h1"` [status badge] ....... [meta] [actions]
 *   2. `description` — Caption1, `colorNeutralForeground2`
 *   3. `children` — a wizard step rail, a secondary tab row
 *
 * ── Two things it deliberately does NOT do ──
 *  - It owns no outer margin. Vertical rhythm belongs to the page's own stack
 *    (`useLayoutStyles().stack`), for the same reason `surfaces.tableCard`
 *    dropped its baked-in `marginTop`: a margin here compounds with whatever
 *    gap the parent already has, and lands at a different value per page.
 *  - It does not wrap `children` in anything. A step rail brings its own
 *    semantics (`<ol>`, `<nav>`); a wrapper would only get in their way.
 */
export interface PageHeaderProps {
  /** The page's name, rendered as the `<h1>`. Plain text — it is the outline entry. */
  title: string;
  /** Renders as a react-router `Link`; the arrow is decorative, so the accessible name is `label` alone. */
  backTo?: { label: string; to: string };
  /** A `StatusBadge`, beside the title. Never a bare `RequestStatus` string. */
  status?: ReactNode;
  /** One sentence under the title, at Caption1. Longer than that belongs on the page. */
  description?: ReactNode;
  /** The right-hand informational slot: autosave state, "Approved by X · 14:32". Not interactive. */
  meta?: ReactNode;
  /**
   * The right-hand action cluster. **At most one `appearance="primary"`** — the
   * primary button is the page's single most likely next step, so a second one
   * makes both ambiguous. Everything else is `secondary` or `transparent`.
   */
  actions?: ReactNode;
  /** A step rail or secondary tab row, on its own row below the description. */
  children?: ReactNode;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    // 8px between the three rows: they are one block, so they sit closer than
    // the 16px that separates this header from the page content below it.
    gap: SPACE.s,
  },

  titleRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: SPACE_X.m,
  },

  /**
   * The title and its description, as one left-hand column.
   *
   * The description lives HERE rather than as a sibling below `titleRow`, and
   * the reason is the narrow viewport: `titleRow` wraps, so a description
   * outside it lands *after* the wrapped action cluster — giving "My requests
   * / [+ New request] / Everything you have raised…", with the button between
   * the heading and the sentence describing it. Keeping the two together makes
   * the wrap order title → description → actions at every width. Seen on a
   * 390px render; the DOM order alone never looked wrong.
   */
  titleColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.xs,
    minWidth: '0',
    flexGrow: 1,
  },

  /** Back link, title and status travel together and wrap as a unit. */
  titleGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: SPACE_X.s,
    minWidth: '0',
  },

  /**
   * `display: block` because Fluent's typography presets render an inline
   * `<span>`-shaped box even when `as` makes the element an `<h1>`; and
   * `margin: 0` because the user agent gives `<h1>` a 0.67em margin that would
   * otherwise reintroduce a second, off-grid vertical rhythm the page cannot
   * see or control.
   */
  title: {
    display: 'block',
    margin: SPACE.none,
    // A long title wraps rather than widening the canvas — Teams forbids
    // sideways scrolling of the tab.
    overflowWrap: 'anywhere',
  },

  /**
   * A native `<a>` does not inherit `color` from `FluentProvider` — it falls
   * back to the user agent's own link colour, the same class of bug that
   * rendered the Dashboard's stat tiles blank in high contrast (CLAUDE.md log
   * entry 54). The token is explicit for that reason.
   */
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: SPACE_X.xs,
    color: tokens.colorBrandForeground1,
    textDecorationLine: 'none',
    ':hover': { textDecorationLine: 'underline' },
  },

  /** Meta and actions share the right-hand end of the title row. */
  side: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: SPACE_X.m,
    '@media (max-width: 700px)': {
      width: '100%',
      justifyContent: 'flex-start',
    },
  },

  meta: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACE_X.s,
    color: tokens.colorNeutralForeground2,
  },

  /**
   * Wireframe: "primary action is full width at 44px on mobile". Below the
   * breakpoint the cluster takes the whole row and its buttons share it
   * equally, so the next step is a thumb-sized target rather than a 32px
   * control tucked against the right edge. The 44px itself comes from
   * `useResponsiveStyles().touchTargets` on the root, which already owns that
   * number for the whole app.
   */
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: SPACE_X.s,
    '@media (max-width: 700px)': {
      width: '100%',
      '& > *': { flexGrow: '1' },
    },
  },

  description: {
    display: 'block',
    margin: SPACE.none,
    color: tokens.colorNeutralForeground2,
  },
});

export function PageHeader({
  title,
  backTo,
  status,
  description,
  meta,
  actions,
  children,
}: PageHeaderProps) {
  const styles = useStyles();
  const responsive = useResponsiveStyles();

  return (
    <div className={mergeClasses(styles.root, responsive.touchTargets)}>
      <div className={styles.titleRow}>
        <div className={styles.titleColumn}>
          <div className={styles.titleGroup}>
            {backTo && (
              <RouterLink to={backTo.to} className={styles.backLink}>
                {/* Decorative: "leftwards arrow" in the accessible name would be
                    read out before the destination, which is the useful part. */}
                <span aria-hidden="true">←</span>
                <Body1 as="span">{backTo.label}</Body1>
              </RouterLink>
            )}
            <Title3 as="h1" className={styles.title}>
              {title}
            </Title3>
            {status}
          </div>

          {description !== undefined && (
            <Caption1 as="p" className={styles.description}>
              {description}
            </Caption1>
          )}
        </div>

        {/* Tested for `undefined` rather than truthiness: a caller may legitimately
            pass an empty fragment, and an empty right-hand cluster still changes
            how the title row distributes its space. */}
        {(meta !== undefined || actions !== undefined) && (
          <div className={styles.side}>
            {meta !== undefined && <div className={styles.meta}>{meta}</div>}
            {actions !== undefined && <div className={styles.actions}>{actions}</div>}
          </div>
        )}
      </div>

      {children}
    </div>
  );
}
